// MCHD Room Scheduler - Security Stats Card Component
// Card component for displaying security statistics

import {
  Card,
  Group,
  Text,
  Progress,
  Stack,
  Box,
  Badge,
  Grid,
  RingProgress,
  Center,
  ThemeIcon,
  Divider,
} from '@mantine/core';
import {
  IconShield,
  IconAlertCircle,
  IconClock,
  IconBan,
  IconCheck,
  IconTrendingUp,
  IconTrendingDown,
  IconMinus,
} from '@tabler/icons-react';

interface SecurityStatsCardProps {
  stats: {
    totalUsers: number;
    activeUsers: number;
    failedAttempts: number;
    lockedAccounts: number;
    activeSessions: number;
    securityScore: number;
  };
  previousStats?: {
    totalUsers: number;
    activeUsers: number;
    failedAttempts: number;
    lockedAccounts: number;
    activeSessions: number;
    securityScore: number;
  };
}

/**
 * Security statistics card component
 * @param props - Component props
 * @returns JSX element
 */
export function SecurityStatsCard({ stats, previousStats }: SecurityStatsCardProps): JSX.Element {
  const getSecurityLevel = (score: number) => {
    if (score >= 90) return { level: 'Excellent', color: 'green', icon: IconCheck };
    if (score >= 70) return { level: 'Good', color: 'blue', icon: IconShield };
    if (score >= 50) return { level: 'Fair', color: 'yellow', icon: IconAlertCircle };
    return { level: 'Poor', color: 'red', icon: IconBan };
  };

  const getTrendIcon = (current: number, previous: number) => {
    if (current > previous) return IconTrendingUp;
    if (current < previous) return IconTrendingDown;
    return IconMinus;
  };

  const getTrendColor = (current: number, previous: number, reverse = false) => {
    if (current > previous) return reverse ? 'red' : 'green';
    if (current < previous) return reverse ? 'green' : 'red';
    return 'gray';
  };

  const securityLevel = getSecurityLevel(stats.securityScore);
  const SecurityIcon = securityLevel.icon;

  const calculateTrend = (current: number, previous: number) => {
    if (previous === 0) return 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="lg">
        {/* Security Score Overview */}
        <Box>
          <Group justify="space-between" mb="md">
            <Text size="lg" fw={600}>
              Security Overview
            </Text>
            <Badge
              color={securityLevel.color}
              variant="light"
              size="lg"
              leftSection={<SecurityIcon size={16} />}
            >
              {securityLevel.level}
            </Badge>
          </Group>
          
          <Center>
            <RingProgress
              size={120}
              thickness={12}
              sections={[
                { value: stats.securityScore, color: securityLevel.color }
              ]}
              label={
                <Center>
                  <Stack gap="xs" align="center">
                    <Text size="xl" fw={700} c={securityLevel.color}>
                      {stats.securityScore}
                    </Text>
                    <Text size="xs" c="dimmed">
                      Security Score
                    </Text>
                  </Stack>
                </Center>
              }
            />
          </Center>
        </Box>

        <Divider />

        {/* Key Metrics */}
        <Box>
          <Text size="md" fw={600} mb="md">
            Key Metrics
          </Text>
          
          <Grid>
            <Grid.Col span={6}>
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm" fw={500}>Total Users</Text>
                  <Group gap="xs">
                    <Text size="sm" fw={600}>{stats.totalUsers}</Text>
                    {previousStats && (
                      <ThemeIcon
                        size="xs"
                        color={getTrendColor(stats.totalUsers, previousStats.totalUsers)}
                        variant="light"
                      >
                        {(() => {
                          const Icon = getTrendIcon(stats.totalUsers, previousStats.totalUsers);
                          return <Icon size={12} />;
                        })()}
                      </ThemeIcon>
                    )}
                  </Group>
                </Group>
                <Progress
                  value={100}
                  color="blue"
                  size="xs"
                />
                {previousStats && (
                  <Text size="xs" c="dimmed">
                    {calculateTrend(stats.totalUsers, previousStats.totalUsers) > 0 ? '+' : ''}
                    {calculateTrend(stats.totalUsers, previousStats.totalUsers)}% from last period
                  </Text>
                )}
              </Stack>
            </Grid.Col>

            <Grid.Col span={6}>
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm" fw={500}>Active Users</Text>
                  <Group gap="xs">
                    <Text size="sm" fw={600}>{stats.activeUsers}</Text>
                    {previousStats && (
                      <ThemeIcon
                        size="xs"
                        color={getTrendColor(stats.activeUsers, previousStats.activeUsers)}
                        variant="light"
                      >
                        {(() => {
                          const Icon = getTrendIcon(stats.activeUsers, previousStats.activeUsers);
                          return <Icon size={12} />;
                        })()}
                      </ThemeIcon>
                    )}
                  </Group>
                </Group>
                <Progress
                  value={stats.totalUsers > 0 ? (stats.activeUsers / stats.totalUsers) * 100 : 0}
                  color="green"
                  size="xs"
                />
                {previousStats && (
                  <Text size="xs" c="dimmed">
                    {calculateTrend(stats.activeUsers, previousStats.activeUsers) > 0 ? '+' : ''}
                    {calculateTrend(stats.activeUsers, previousStats.activeUsers)}% from last period
                  </Text>
                )}
              </Stack>
            </Grid.Col>

            <Grid.Col span={6}>
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm" fw={500}>Failed Attempts</Text>
                  <Group gap="xs">
                    <Text size="sm" fw={600}>{stats.failedAttempts}</Text>
                    {previousStats && (
                      <ThemeIcon
                        size="xs"
                        color={getTrendColor(stats.failedAttempts, previousStats.failedAttempts, true)}
                        variant="light"
                      >
                        {(() => {
                          const Icon = getTrendIcon(stats.failedAttempts, previousStats.failedAttempts);
                          return <Icon size={12} />;
                        })()}
                      </ThemeIcon>
                    )}
                  </Group>
                </Group>
                <Progress
                  value={Math.min(stats.failedAttempts * 10, 100)}
                  color={stats.failedAttempts > 10 ? 'red' : stats.failedAttempts > 5 ? 'yellow' : 'green'}
                  size="xs"
                />
                {previousStats && (
                  <Text size="xs" c="dimmed">
                    {calculateTrend(stats.failedAttempts, previousStats.failedAttempts) > 0 ? '+' : ''}
                    {calculateTrend(stats.failedAttempts, previousStats.failedAttempts)}% from last period
                  </Text>
                )}
              </Stack>
            </Grid.Col>

            <Grid.Col span={6}>
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm" fw={500}>Locked Accounts</Text>
                  <Group gap="xs">
                    <Text size="sm" fw={600}>{stats.lockedAccounts}</Text>
                    {previousStats && (
                      <ThemeIcon
                        size="xs"
                        color={getTrendColor(stats.lockedAccounts, previousStats.lockedAccounts, true)}
                        variant="light"
                      >
                        {(() => {
                          const Icon = getTrendIcon(stats.lockedAccounts, previousStats.lockedAccounts);
                          return <Icon size={12} />;
                        })()}
                      </ThemeIcon>
                    )}
                  </Group>
                </Group>
                <Progress
                  value={Math.min(stats.lockedAccounts * 20, 100)}
                  color={stats.lockedAccounts > 5 ? 'red' : stats.lockedAccounts > 2 ? 'yellow' : 'green'}
                  size="xs"
                />
                {previousStats && (
                  <Text size="xs" c="dimmed">
                    {calculateTrend(stats.lockedAccounts, previousStats.lockedAccounts) > 0 ? '+' : ''}
                    {calculateTrend(stats.lockedAccounts, previousStats.lockedAccounts)}% from last period
                  </Text>
                )}
              </Stack>
            </Grid.Col>
          </Grid>
        </Box>

        <Divider />

        {/* Session Information */}
        <Box>
          <Text size="md" fw={600} mb="md">
            Session Information
          </Text>
          
          <Group justify="space-between">
            <Group gap="sm">
              <ThemeIcon color="blue" variant="light">
                <IconClock size={16} />
              </ThemeIcon>
              <Box>
                <Text size="sm" fw={500}>Active Sessions</Text>
                <Text size="xs" c="dimmed">Currently online</Text>
              </Box>
            </Group>
            <Text size="lg" fw={700} c="blue">
              {stats.activeSessions}
            </Text>
          </Group>
        </Box>
      </Stack>
    </Card>
  );
}
